import React, { useMemo, useState } from 'react'
import Button from '../ButtonNew'
import Text from '../Text'
import BottomSheet from '../BottomSheet'
import CloseFilledIcon from '../Icons/CloseFilledIcon'
import VariantBottomSheet from './VariantBottomSheet'
import { useNavigate } from 'react-router-dom'
import { getUrlParams } from '../../utils/helpers/common'
import { trackEvent } from '../../analytics'
import { EVENTS } from '../../analytics/events'
import {
  MYSAATHI_VARIANT_EVENT,
  MYSAATHI_VARIANT_STORAGE_KEY,
} from '../../constants/sema-constants'

const persistVariantSelection = (variantId) => {
  if (!variantId) return
  try {
    localStorage.setItem(MYSAATHI_VARIANT_STORAGE_KEY, variantId)
  } catch (_) {
    // ignore storage failures
  }
  trackEvent(EVENTS.CLICK, {
    action: 'mysaathi_variant_selected',
    mysaathi_variant: variantId,
  })
  window.dispatchEvent(
    new CustomEvent(MYSAATHI_VARIANT_EVENT, {
      detail: { variant: variantId },
    }),
  )
}

const FooterSection = ({ data, showStickyButton }) => {
  const navigate = useNavigate()
  const params = getUrlParams()
  const text = data?.infobanner?.text1
  const subText = data?.infobanner?.text2
  const goodflipIcon = data?.infobanner?.replaceImage1
  const partnerIcon = data?.infobanner?.partnerImage

  const buttons = Array.isArray(data?.buttons) ? data?.buttons : []

  // 'variant' | 'doctorKit' | legacy bottomSheet object | null
  const [sheetType, setSheetType] = useState(null)
  const [activeButton, setActiveButton] = useState(null)
  const [legacySheet, setLegacySheet] = useState(null)
  const [selectedVariant, setSelectedVariant] = useState(null)

  const variantSheet = activeButton?.variantBottomSheet
  const doctorKitSheet = useMemo(() => {
    if (!activeButton) return null
    const byVariant =
      selectedVariant &&
      activeButton?.doctorKitBottomSheetByVariant?.[selectedVariant]
    return byVariant || activeButton?.doctorKitBottomSheet || null
  }, [activeButton, selectedVariant])

  const proceedDisabled =
    Boolean(variantSheet?.proceedDisabledUntilSelected) && !selectedVariant

  const closeSheets = () => {
    setSheetType(null)
    setLegacySheet(null)
    setActiveButton(null)
    setSelectedVariant(null)
  }

  const handleRegisterNavigate = (variantId) => {
    closeSheets()
    const nextParams = { ...params }
    if (variantId) {
      nextParams.mysaathi_variant = variantId
    }
    const queryString = new URLSearchParams(
      Object.entries(nextParams).reduce((acc, [key, value]) => {
        if (value != null && value !== '') acc[key] = value
        return acc
      }, {}),
    ).toString()
    navigate(`register${queryString ? `?${queryString}` : ''}`)
  }

  const openDoctorKitOrRegister = (variantId) => {
    if (activeButton?.showDoctorKitBottomSheet) {
      setSheetType('doctorKit')
      return
    }
    handleRegisterNavigate(variantId)
  }

  const handleVariantProceed = () => {
    if (proceedDisabled) return
    persistVariantSelection(selectedVariant)
    openDoctorKitOrRegister(selectedVariant)
  }

  const handleCtaClick = (button) => {
    if (button?.previewUrl) {
      window.open(button?.previewUrl, '_blank')
      return
    }

    if (button?.variantBottomSheet) {
      setActiveButton(button)
      setSelectedVariant(null)
      setLegacySheet(null)
      setSheetType('variant')
      return
    }

    if (button?.showDoctorKitBottomSheet) {
      setActiveButton(button)
      setLegacySheet(null)
      setSheetType('doctorKit')
      return
    }

    if (button?.showBottomSheet) {
      setActiveButton(button)
      setLegacySheet(button?.bottomSheet)
      setSheetType('legacy')
      return
    }

    handleRegisterNavigate()
  }

  function renderTitleWithImage(title, image) {
    if (!title?.includes('replaceImage1') || !image) {
      return (
        <Text
          tag="div"
          variant="label"
          size="small"
          className="text-[#A5508F] !leading-[1.3]"
          dangerouslySetInnerHTML={{ __html: title }}
        />
      )
    }
    const parts = title?.split('replaceImage1')
    return (
      <Text
        tag="div"
        variant="label"
        size="small"
        className="text-[#A5508F] !leading-[1.3] "
      >
        {parts[0] && (
          <span dangerouslySetInnerHTML={{ __html: parts[0].trim() }} />
        )}
        <img
          src={image}
          alt=""
          className="h-5 w-[46px] object-contain inline-block mx-0.5"
        />

        {parts[1] && (
          <span dangerouslySetInnerHTML={{ __html: parts[1].trim() }} />
        )}
      </Text>
    )
  }

  const legacySheetOpen = sheetType === 'doctorKit' || sheetType === 'legacy'
  const legacySheetTitle =
    sheetType === 'doctorKit'
      ? doctorKitSheet?.title || 'Before you register'
      : legacySheet?.title || 'Before you register'

  return (
    <footer
      className={`sticky w-full max-w-lg mx-auto left-0 right-0 pb-3 bg-white z-50 transition-all duration-300 ease-in-out ${
        showStickyButton ? 'bottom-0' : 'bottom-[-190px]'
      }`}
      style={{
        borderTop: '1px solid rgba(0, 0, 0, 0.1)',
      }}
    >
      <div className="space-y-0.5 [background:linear-gradient(0deg,_rgba(26,_148,_150,_0.12)_0%,_rgba(26,_148,_150,_0.12)_100%),_#FFF] px-3 py-2">
        <Text
          tag="div"
          variant="label"
          size="small"
          className="text-[#A5508F] flex items-center gap-1"
        >
          {renderTitleWithImage(text, goodflipIcon)}
          {partnerIcon ? (
            <img
              src={partnerIcon}
              alt="Partner"
              className="w-[48px] h-[18px] inline-flex absolute left-[52%] top-[20%]"
            />
          ) : null}
        </Text>
      </div>
      {buttons?.length > 0 && (
        <div className={`flex gap-4 px-2 py-1`}>
          {buttons?.map((button) => (
            <Button
              key={button?.label}
              children={button?.label}
              onClick={() => handleCtaClick(button)}
              fullWidth={true}
              size="xSmall"
              variant={button?.type || 'contained'}
              isFlatButton
              color={button?.color || 'brand-green'}
            />
          ))}
        </div>
      )}

      <VariantBottomSheet
        show={sheetType === 'variant'}
        config={variantSheet}
        selectedVariant={selectedVariant}
        onSelect={setSelectedVariant}
        onClose={closeSheets}
        onProceed={handleVariantProceed}
        proceedDisabled={proceedDisabled}
      />

      <BottomSheet
        show={legacySheetOpen}
        onClose={closeSheets}
        title={legacySheetTitle}
        closeIcon={<CloseFilledIcon />}
        headerClassName="!px-3.5 !py-2"
      >
        {legacySheetOpen ? (
          <div className="p-4 space-y-4">
            {(
              sheetType === 'doctorKit'
                ? doctorKitSheet?.stripImage
                : legacySheet?.stripImage
            ) ? (
              <img
                src={
                  sheetType === 'doctorKit'
                    ? doctorKitSheet?.stripImage
                    : legacySheet?.stripImage
                }
                alt="Strip"
                height={40}
                width={250}
                className="mx-auto"
              />
            ) : null}
            {(
              sheetType === 'doctorKit'
                ? doctorKitSheet?.description
                : legacySheet?.description
            ) ? (
              <Text
                variant="body"
                size="medium"
                tag="p"
                dangerouslySetInnerHTML={{
                  __html:
                    sheetType === 'doctorKit'
                      ? doctorKitSheet?.description
                      : legacySheet?.description,
                }}
              />
            ) : null}
            <Button
              variant="contained"
              size="xSmall"
              onClick={() => handleRegisterNavigate(selectedVariant)}
              fullWidth={true}
              isFlatButton
            >
              {activeButton?.cta?.label ||
                legacySheet?.cta?.label ||
                'Register Now'}
            </Button>
          </div>
        ) : null}
      </BottomSheet>
    </footer>
  )
}

export default FooterSection
